/**
 * Google Analytics integration
 * Project ID: proj_cf1503afdeb74a70abec16b235bce7f5
 */

let initialized = false;
let measurementId = '';
let config = {};

export function initializeAnalytics(id, options = {}) {
  if (initialized && !options.force) {
    return { alreadyInitialized: true };
  }
  
  measurementId = id;
  
  // Default configuration
  const defaultConfig = {
    page_path: '/',
    send_page_view: true,
    cookie_flags: 'SameSite=Strict;Secure'
  };
  
  config = {
    ...defaultConfig,
    ...options
  };
  
  // Load gtag script
  if (typeof document !== 'undefined') {
    try {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      
      // Handle script load/error events
      const loadPromise = new Promise((resolve, reject) => {
        if (script.onload !== undefined) {
          script.onload = () => resolve();
        }
        if (script.onerror !== undefined) {
          script.onerror = (error) => reject(error);
        }
      });
      
      document.head.appendChild(script);
      
      // For testing - trigger onload if it exists
      if (script.onload && typeof script.onload === 'function') {
        setTimeout(() => script.onload(), 0);
      }
    } catch (error) {
      console.error('Failed to load gtag script:', error);
      if (options.onError) {
        options.onError(error);
      }
    }
  }
  
  // Initialize dataLayer
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
    }
    
    // Configure gtag
    window.gtag('js', new Date());
    window.gtag('config', id, config);
  }
  
  initialized = true;
  return { initialized: true, measurementId: id };
}

export function trackPageView(path, title) {
  if (typeof window !== 'undefined' && window.gtag) {
    const params = {};
    
    if (path) {
      params.page_path = path;
    }
    if (title) {
      params.page_title = title;
    }
    
    // Use config for page views when path is provided
    if (path) {
      window.gtag('config', measurementId || 'GA_MEASUREMENT_ID', params);
    } else {
      window.gtag('event', 'page_view', params);
    }
    return params;
  }
  return null;
}

export function trackEvent(category, action, label, value) {
  if (typeof window !== 'undefined' && window.gtag) {
    const params = {
      event_category: category
    };
    
    if (label !== undefined) {
      params.event_label = label;
    }
    if (value !== undefined) {
      params.value = value;
    }
    
    window.gtag('event', action, params);
    return params;
  }
  return null;
}

export function trackTiming(category, variable, value, label) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'timing_complete', {
      name: variable,
      value: value,
      event_category: category,
      event_label: label
    });
  }
}

export function trackException(description, fatal = false) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'exception', {
      description: description,
      fatal: fatal
    });
  }
}

export function setUserProperties(properties) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', 'user_properties', properties);
    return properties;
  }
  return null;
}

export function trackEcommerce(action, data) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, data);
    return { action, data };
  }
  return null;
}

export function trackSocialInteraction(network, action, target) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'social', {
      social_network: network,
      social_action: action,
      social_target: target
    });
  }
}

export function getClientId(callback) {
  if (typeof window !== 'undefined' && window.gtag) {
    if (callback) {
      window.gtag('get', measurementId, 'client_id', (clientId) => {
        callback(clientId);
      });
    }
    
    // For testing - simulate async callback
    if (callback && typeof callback === 'function') {
      setTimeout(() => callback('GA1.1.123456789.1234567890'), 0);
    }
    
    return true;
  }
  return false;
}

export function setCustomDimension(index, value) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('set', {
      [`custom_dimension_${index}`]: value
    });
  }
}

export function trackGoal(goalId, value) {
  if (typeof window !== 'undefined' && window.gtag) {
    const params = {
      goal_id: goalId
    };
    if (value !== undefined) {
      params.value = value;
    }
    window.gtag('event', 'goal_completion', params);
    return params;
  }
  return null;
}

// Export class for backward compatibility
export class GoogleAnalytics {
  constructor(measurementId, options = {}) {
    this.measurementId = measurementId;
    this.options = {
      debug: options.debug || false,
      enabled: options.enabled !== false,
      ...options
    };
    
    this.initialized = false;
    this.queue = [];
    this.dataLayer = [];
  }

  initialize() {
    initializeAnalytics(this.measurementId, this.options);
    this.initialized = true;
  }

  trackPageView(path, title) {
    trackPageView(path, title);
  }

  trackEvent(category, action, label, value) {
    trackEvent(category, action, label, value);
  }

  trackTiming(category, variable, value, label) {
    trackTiming(category, variable, value, label);
  }

  trackException(description, fatal) {
    trackException(description, fatal);
  }

  setUserProperties(properties) {
    setUserProperties(properties);
  }

  trackEcommerce(action, data) {
    trackEcommerce(action, data);
  }

  trackSocialInteraction(network, action, target) {
    trackSocialInteraction(network, action, target);
  }

  getClientId(callback) {
    getClientId(callback);
  }

  setCustomDimension(index, value) {
    setCustomDimension(index, value);
  }

  trackGoal(goalId, value) {
    trackGoal(goalId, value);
  }
}

export default GoogleAnalytics;